/**
 * Draft shapes of the container's rules (verdict satellite): the flat
 * ALL/ANY condition list both the terrible rule and the Phase 10 scaling
 * rule speak, plus their guard numbers. `advanced` marks a stored tree
 * deeper than one flat group — edited via API, preserved as saved.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenGatewayBlockScope, DropGenHitAmountMode, DropGenHitWhen, DropGenTextMix, DropGenTimesMode, DropGenVerdictSource } from '@trafficpulse/shared';

/** A threshold on a metric of the day's drops added up. */
export interface DraftMetricRow {
  metric: string;
  op: string;
  value: number;
  /** Phase 28 — the condition inside a NOT ("NOT Delivered Rate < 85 %"). */
  not?: boolean;
  tag?: undefined;
  negate?: undefined;
}

/** Phase 38 — the judged thing (the list of the day, the gateway) is / is not tagged X. */
export interface DraftTagRow {
  tag: string;
  negate?: boolean;
  not?: boolean;
  metric?: undefined;
  op?: undefined;
  value?: undefined;
}

export type DraftVerdictRow = DraftMetricRow | DraftTagRow;

export const isTagRow = (row: DraftVerdictRow): row is DraftTagRow => typeof row.tag === 'string';

export interface DraftVerdict {
  enabled: boolean;
  join: 'AND' | 'OR';
  rows: DraftVerdictRow[];
  advanced: boolean;
  minSampleSent: number;
  evaluationDelayMin: number;
  /** 20/10/2026 — FAIL only when this many settled drops each met the rule on their own numbers; 1 = the day's total. */
  timesN: number;
  /** 20/10/2026 — how those drops count: TOTAL (good ones in between allowed) or ROW (a good drop resets). */
  timesMode: DropGenTimesMode;
  /** Phase 31 — a burned list rests this many days, then comes back; null = until Reset pool. */
  burnRestDays: number | null;
  /** Phase 31 — the amount on its first day back; null = the usual amount. */
  returnTestAmount: number | null;
  /** Phase 39 — which numbers the card judges: today's drops, or the list's own stats over a range. */
  source: DropGenVerdictSource;
}

/** One flat ALL/ANY rule as the editor speaks it. */
export interface DraftFlatRule {
  join: 'AND' | 'OR';
  rows: DraftVerdictRow[];
  advanced: boolean;
}

/** Phase 17 — "the gateway is terrible when …" + the sample a route needs. */
export interface DraftGatewayVerdict {
  /** Off until the operator turns gateway checks on. The rule is kept. */
  enabled: boolean;
  rule: DraftFlatRule;
  minSampleSent: number;
  /** 20/10/2026 — blocked only when this many of its settled drops each met the rule; 1 = its day's total. */
  timesN: number;
  timesMode: DropGenTimesMode;
  /** Phase 39 — today's drops through the gateway, or the gateway's own stats over a range. */
  source: DropGenVerdictSource;
  /** 14/10/2026 — blocked in this container, or the whole gateway in every container of the country. */
  blockScope: DropGenGatewayBlockScope;
}

/** Phase 10 — "the list is performing when … → grow by X % up to N, unless …". */
export interface DraftScaling {
  performing: DraftFlatRule;
  stepPct: number;
  /** null = no amount cap (the hard step cap still applies). */
  maxAmount: number | null;
  guard: DraftFlatRule;
  /** Phase 31 — "the list is weak when …": one step down per check. */
  weak: DraftFlatRule;
  shrinkPct: number;
  /** null = a tenth of the base amount. */
  minAmount: number | null;
  /** Phase 39 — one source for the three rules (they judge one bag). */
  source: DropGenVerdictSource;
}

/** Phase 38 — "the drop hits again when … → send again now with …": the per-drop
 *  rule, its own min sample, and the action (how the amount is made, cap, chain length). */
export interface DraftHitAgain {
  /** Off until the operator turns hit again on. The rule is kept. */
  enabled: boolean;
  rule: DraftFlatRule;
  minSampleSent: number;
  mode: DropGenHitAmountMode;
  pct: number;
  fixed: number | null;
  cap: number | null;
  /** null = no limit (the default). */
  maxTimes: number | null;
  /** 18/10/2026 — when the new drop may go out: any time the window is open, or only in the container's hours. */
  when: DropGenHitWhen;
  /** 21/10/2026 — Texts: null = the texts of the drop that hit; a mix = new texts. A Verdicts value. */
  texts: DropGenTextMix | null;
  /** 22/10/2026 — Duplicate: the new drop goes out with SMSWiser's DUPLICATE option on (default) or off. */
  duplicate: boolean;
  /** Phase 39 — the drop's own stats, or its list's own stats over a range. */
  source: DropGenVerdictSource;
}

/** Phase 20 — one auto-send layer as the editor edits it: a campaign group's
 *  (on its master config) or a container's own on top. */
export interface DraftAutoSend {
  dispatchMode?: 'DUE_TIME' | 'SCHEDULE_AHEAD';
  enabled: boolean;
  /** Phase 29 — shadow mode: decides like a send, sends nothing. */
  shadow: boolean;
  noCap: boolean;
  cap: number;
  /** 23/10/2026 — the stop-loss's own switch: off keeps the rule, nothing is judged. */
  stopLossEnabled: boolean;
  join: 'AND' | 'OR';
  rows: DraftVerdictRow[];
  advanced: boolean;
  minSample: number;
  delayMin: number;
}
